/**
 * AgencyDashboardAggregatingAllClientPerformancPage — agency aggregated view
 * rolling up performance across all clients under one roof.
 *
 * Layout:
 *   - Topbar: title + client switcher (All clients | pick one)
 *   - 4 KPI tiles (clients, clicks, conversions, spend) — totals for the
 *     currently active client filter
 *   - Per-client KPI table with totals footer row
 *
 * Data source: GET /api/v1/agency-dashboard-aggregating-all-client-performanc
 * returns `{ items: SavedView[] }`. Each saved view carries a `filters.client_ids`
 * and optional numeric widget counters on widget[i]. We fold every saved view
 * into a per-client aggregate so the page works even when the live rollup
 * endpoint is not reachable from the dash (client-side aggregation is the
 * source of truth for this page).
 *
 * Pure functions `aggregate()` and `filterByClient()` are exported so the
 * vitest data-layer suite can pin down the math + filter behaviour.
 *
 * Accent: green-600 / green-700 / green-50 only. NO emerald, NO blue, NO
 * orange, NO purple, NO dark mode.
 */
import { useMemo, useState } from "react";
import { useAgencyDashboardAggregatingAllClientPerformancList } from "./hooks";
import type { AgencyDashboardAggregatingAllClientPerformancRow } from "./api";

// ---------------------------------------------------------------------------
// Shared styles — white cards, zinc hairlines, 8px rounding, green accent.
// No gradients, no emerald, no blue, no orange, no purple, no dark mode.
// ---------------------------------------------------------------------------
const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const TD_NUM = "px-3 py-2 text-sm text-zinc-900 tabular-nums";
const BTN_BASE =
	"rounded-[6px] border px-3 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-green-600";
const BTN_ACTIVE = `${BTN_BASE} border-green-700 bg-green-600 text-white shadow-sm hover:bg-green-700`;
const BTN_IDLE = `${BTN_BASE} border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50`;

// ---------------------------------------------------------------------------
// Types — aggregated per-client shape the table renders.
// ---------------------------------------------------------------------------
export interface ClientKpis {
	client_id: string;
	views: number;
	clicks: number;
	conversions: number;
	spend_cents: number;
}

export interface AggregatedTotals {
	clients: number;
	views: number;
	clicks: number;
	conversions: number;
	spend_cents: number;
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for vitest data-layer suite).
// ---------------------------------------------------------------------------

/**
 * Fold the saved-view list into a per-client KPI aggregate. Each saved view
 * contributes `+1 view` to every client_id listed in its filters, plus any
 * numeric `clicks / conversions / spend_cents` fields attached to its widgets
 * (tolerated missing — defaults to 0). Output is deterministic sorted by
 * client_id to make the table render stable across renders.
 */
export function aggregate(
	items: AgencyDashboardAggregatingAllClientPerformancRow[],
): ClientKpis[] {
	const acc = new Map<string, ClientKpis>();

	for (const row of items) {
		const filters = (row as { filters?: unknown }).filters;
		const clientIds =
			filters && typeof filters === "object" && Array.isArray((filters as { client_ids?: unknown }).client_ids)
				? ((filters as { client_ids: unknown[] }).client_ids.filter(
						(c): c is string => typeof c === "string" && c.length > 0,
					) as string[])
				: [];

		if (clientIds.length === 0) continue;

		// Widgets may carry numeric kpi fields — fold them if present.
		const widgetsRaw = (row as { widgets?: unknown }).widgets;
		const widgets: unknown[] = Array.isArray(widgetsRaw) ? widgetsRaw : [];
		let addClicks = 0;
		let addConvs = 0;
		let addSpend = 0;
		for (const w of widgets) {
			if (!w || typeof w !== "object") continue;
			const wx = w as Record<string, unknown>;
			if (typeof wx.clicks === "number") addClicks += wx.clicks;
			if (typeof wx.conversions === "number") addConvs += wx.conversions;
			if (typeof wx.spend_cents === "number") addSpend += wx.spend_cents;
		}

		for (const cid of clientIds) {
			const prev = acc.get(cid) ?? {
				client_id: cid,
				views: 0,
				clicks: 0,
				conversions: 0,
				spend_cents: 0,
			};
			prev.views += 1;
			prev.clicks += addClicks;
			prev.conversions += addConvs;
			prev.spend_cents += addSpend;
			acc.set(cid, prev);
		}
	}

	return Array.from(acc.values()).sort((a, b) => a.client_id.localeCompare(b.client_id));
}

/**
 * Keep only the selected client (when non-null). `null` means "all clients".
 */
export function filterByClient(
	rows: ClientKpis[],
	clientId: string | null,
): ClientKpis[] {
	if (clientId === null) return rows;
	return rows.filter((r) => r.client_id === clientId);
}

/**
 * Totals footer math — sum of every per-client row in view.
 */
export function totals(rows: ClientKpis[]): AggregatedTotals {
	let views = 0;
	let clicks = 0;
	let conversions = 0;
	let spend = 0;
	for (const r of rows) {
		views += r.views;
		clicks += r.clicks;
		conversions += r.conversions;
		spend += r.spend_cents;
	}
	return {
		clients: rows.length,
		views,
		clicks,
		conversions,
		spend_cents: spend,
	};
}

function fmtInt(n: number): string {
	return Number.isFinite(n) ? Math.round(n).toLocaleString() : "0";
}

function fmtMoney(cents: number): string {
	if (!Number.isFinite(cents)) return "$0.00";
	return `$${(cents / 100).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function AgencyDashboardAggregatingAllClientPerformancPage() {
	const listQuery = useAgencyDashboardAggregatingAllClientPerformancList();
	const rawItems = (listQuery.data?.items ?? []) as AgencyDashboardAggregatingAllClientPerformancRow[];
	const [active, setActive] = useState<string | null>(null);

	const allRows = useMemo(() => aggregate(rawItems), [rawItems]);
	const shown = useMemo(() => filterByClient(allRows, active), [allRows, active]);
	const footer = useMemo(() => totals(shown), [shown]);
	const clientIds = useMemo(() => allRows.map((r) => r.client_id), [allRows]);

	return (
		<main
			data-testid="agency-dashboard-aggregating-all-client-performanc-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8"
		>
			{/* Topbar */}
			<header className="flex flex-col gap-3">
				<div className="flex flex-col gap-1">
					<p className="text-xs font-semibold uppercase tracking-wider text-green-700">
						Agency report
					</p>
					<h1 className="text-2xl font-semibold text-zinc-900">
						Agency dashboard
					</h1>
					<p className="text-sm text-zinc-500">
						Rolled-up performance across every client under this agency.
					</p>
				</div>

				{/* Client switcher */}
				<div
					className="flex flex-wrap gap-2"
					role="group"
					aria-label="Client switcher"
					data-testid="agency-dashboard-client-switcher"
				>
					<button
						type="button"
						data-testid="agency-dashboard-client-all"
						className={active === null ? BTN_ACTIVE : BTN_IDLE}
						onClick={() => setActive(null)}
					>
						All clients
					</button>
					{clientIds.map((cid) => (
						<button
							key={cid}
							type="button"
							data-testid={`agency-dashboard-client-${cid}`}
							className={active === cid ? BTN_ACTIVE : BTN_IDLE}
							onClick={() => setActive(cid)}
						>
							{cid}
						</button>
					))}
				</div>
			</header>

			{/* KPI tiles */}
			<section
				aria-label="Agency totals"
				className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
				data-testid="agency-dashboard-kpi-tiles"
			>
				<KpiTile label="Clients" value={fmtInt(footer.clients)} />
				<KpiTile label="Clicks" value={fmtInt(footer.clicks)} />
				<KpiTile label="Conversions" value={fmtInt(footer.conversions)} />
				<KpiTile label="Spend" value={fmtMoney(footer.spend_cents)} />
			</section>

			{/* Per-client table */}
			<section className={CARD} aria-label="Per-client performance">
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="agency-dashboard-aggregating-all-client-performanc-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : shown.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="agency-dashboard-aggregating-all-client-performanc-empty"
					>
						No client data yet.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="agency-dashboard-aggregating-all-client-performanc-table"
						>
							<thead className="bg-green-50">
								<tr>
									<th className={TH}>Client</th>
									<th className={`${TH} text-right`}>Saved views</th>
									<th className={`${TH} text-right`}>Clicks</th>
									<th className={`${TH} text-right`}>Conversions</th>
									<th className={`${TH} text-right`}>Spend</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{shown.map((r) => (
									<tr
										key={r.client_id}
										data-testid={`agency-dashboard-client-row-${r.client_id}`}
									>
										<td className={TD}>{r.client_id}</td>
										<td className={`${TD_NUM} text-right`}>{fmtInt(r.views)}</td>
										<td className={`${TD_NUM} text-right`}>{fmtInt(r.clicks)}</td>
										<td className={`${TD_NUM} text-right`}>
											{fmtInt(r.conversions)}
										</td>
										<td className={`${TD_NUM} text-right`}>
											{fmtMoney(r.spend_cents)}
										</td>
									</tr>
								))}
							</tbody>
							<tfoot className="border-t-2 border-green-700 bg-green-50">
								<tr data-testid="agency-dashboard-totals-row">
									<td className={`${TD} font-semibold text-green-700`}>
										Totals ({fmtInt(footer.clients)})
									</td>
									<td className={`${TD_NUM} text-right font-semibold`}>
										{fmtInt(footer.views)}
									</td>
									<td className={`${TD_NUM} text-right font-semibold`}>
										{fmtInt(footer.clicks)}
									</td>
									<td className={`${TD_NUM} text-right font-semibold`}>
										{fmtInt(footer.conversions)}
									</td>
									<td className={`${TD_NUM} text-right font-semibold`}>
										{fmtMoney(footer.spend_cents)}
									</td>
								</tr>
							</tfoot>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

function KpiTile({ label, value }: { label: string; value: string }) {
	return (
		<div className={CARD} data-testid={`agency-dashboard-tile-${label.toLowerCase()}`}>
			<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
				{label}
			</p>
			<p className="mt-2 text-2xl font-semibold tabular-nums text-zinc-900">
				{value}
			</p>
		</div>
	);
}

export default AgencyDashboardAggregatingAllClientPerformancPage;
